import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Select,
  Checkbox,
  Input,
  Textarea,
  Alert,
  AlertIcon,
  useToast,
  Divider,
  Badge,
  FormControl,
  FormLabel,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberDecrementStepper,
  NumberIncrementStepper,
  SimpleGrid,
  Spinner,
  Center,
  useColorModeValue,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Api } from 'api';
import { ExpertisePointGroupType, ExpertisePointGroupTypeLabel } from 'types';
import { GenericMessages } from 'core';
import { ImageUploader } from './ImageUploader';

interface ProductVariantOption {
  id: number;
  value: string;
  stock?: number;
}

interface VariantWithOptions {
  id: number;
  name: string;
  options: ProductVariantOption[];
}

interface SaveProductData {
  id?: number;
  name: string;
  title: string;
  price: number;
  description?: string;
  isActive: boolean;
  photoUrls: string[];
  expertisePointGroupType?: ExpertisePointGroupType;
  variantOptions: {
    variantOptionId: number;
    stockQuantity: number;
  }[];
}

interface AdminProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: any; // Düzenleme için mevcut ürün
  onSuccess?: () => void;
}

const validationSchema = yup.object({
  name: yup.string().required(GenericMessages.CannotBeEmpty),
  title: yup.string().required(GenericMessages.CannotBeEmpty),
  price: yup.number().required(GenericMessages.CannotBeEmpty).min(0, 'Fiyat negatif olamaz'),
  expertisePointGroupType: yup.number().required('Marka seçimi zorunlu'),
});

export const AdminProductModal: React.FC<AdminProductModalProps> = ({ isOpen, onClose, product, onSuccess }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<{ [key: number]: number }>({});
  const [selectedVariantType, setSelectedVariantType] = useState<number | null>(null);

  // Dark mode colors
  const textSecondary = useColorModeValue('gray.600', 'gray.300');
  const errorColor = useColorModeValue('red.500', 'red.300');
  const variantBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Fetch variant options
  const {
    data: variantData,
    isLoading: isLoadingVariants,
    error: variantError,
  } = useQuery<VariantWithOptions[]>(
    'variantWithOptions',
    async () => {
      const response = await Api.Product.getVariantWithOptions();
      return response.data || [];
    },
    {
      enabled: isOpen,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Save product mutation
  const saveProductMutation = useMutation(
    async (data: SaveProductData) => {
      const payload = {
        id: product?.id,
        name: data.name,
        title: data.title,
        price: data.price,
        description: data.description,
        isActive: data.isActive,
        photoUrls: data.photoUrls,
        expertisePointGroupType: data.expertisePointGroupType,
        variantOptions: data.variantOptions,
      };

      await Api.Product.saveProduct(payload);
    },
    {
      onSuccess: () => {
        toast({
          title: 'Başarılı',
          description: product ? 'Ürün başarıyla güncellendi.' : 'Ürün başarıyla eklendi.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        queryClient.invalidateQueries('adminEStoreProducts');

        // Reset variant state after successful save (form will reset when modal closes)
        setPhotoUrls([]);
        setSelectedVariants({});
        setSelectedVariantType(null);

        onSuccess?.();
        onClose();
      },
      onError: (error) => {
        console.error('Error saving product:', error);
        toast({
          title: 'Hata',
          description: 'Ürün kaydedilirken bir hata oluştu.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      },
    },
  );

  const formik = useFormik({
    initialValues: {
      name: '',
      title: '',
      price: 0,
      description: '',
      isActive: true,
      photoUrls: [''],
      expertisePointGroupType: ExpertisePointGroupType.OccoExpertise,
      variantOptions: [],
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const variantOptions = Object.entries(selectedVariants).map(([variantOptionId, stockQuantity]) => ({
        variantOptionId: parseInt(variantOptionId, 10),
        stockQuantity,
      }));

      await saveProductMutation.mutateAsync({
        ...values,
        photoUrls,
        variantOptions,
      });
    },
  });

  // Photo URLs management
  const handlePhotoUrlsChange = (urls: string[]) => {
    setPhotoUrls(urls);
  };

  // Variant management
  const handleVariantTypeChange = (variantTypeId: string) => {
    const parsedId = parseInt(variantTypeId, 10);
    setSelectedVariantType(parsedId);
    setSelectedVariants({}); // Clear existing selections when variant type changes
  };

  const handleVariantChange = (variantOptionId: number, stockQuantity: number) => {
    const selectedVariant = variantData?.find((v) => v.id === selectedVariantType);

    if (selectedVariant?.name.toLowerCase().includes('adet')) {
      // For "adet" type, only allow one selection
      if (stockQuantity > 0) {
        setSelectedVariants({ [variantOptionId]: stockQuantity });
      } else {
        setSelectedVariants({});
      }
    }

    // For other types (like "beden"), allow multiple selections
    if (!selectedVariant?.name.toLowerCase().includes('adet')) {
      if (stockQuantity > 0) {
        setSelectedVariants((prev) => ({
          ...prev,
          [variantOptionId]: stockQuantity,
        }));
      } else {
        setSelectedVariants((prev) => {
          const newVariants = { ...prev };
          delete newVariants[variantOptionId];
          return newVariants;
        });
      }
    }
  };

  const handleRadioVariantChange = (variantOptionId: string) => {
    const optionId = parseInt(variantOptionId, 10);
    // For radio selection, preserve existing stock or set to 1 if not set
    const currentStock = selectedVariants[optionId] || 1;
    setSelectedVariants({ [optionId]: currentStock });
  };

  const handleAdetStockChange = (variantOptionId: number, stockQuantity: number) => {
    if (stockQuantity > 0) {
      setSelectedVariants({ [variantOptionId]: stockQuantity });
    } else {
      setSelectedVariants({});
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Set photo URLs
      setPhotoUrls(product?.photoUrls && product.photoUrls.length > 0 ? product.photoUrls : []);

      // Only clear variants if this is a new product (not editing)
      if (!product) {
        setSelectedVariants({});
        setSelectedVariantType(null);
      }

      // Set formik values
      formik.setValues({
        name: product?.name || '',
        title: product?.title || '',
        price: product?.price || 0,
        description: product?.description || '',
        isActive: product?.isActive ?? true,
        photoUrls: product?.photoUrls || [''],
        expertisePointGroupType: product?.groupType || ExpertisePointGroupType.OccoExpertise,
        variantOptions: [],
      });

      // Initial variant setup will be handled by separate useEffect when variantData loads
    } else {
      // Reset when modal closes
      formik.resetForm();
      setPhotoUrls([]);
      setSelectedVariants({});
      setSelectedVariantType(null);
    }
  }, [isOpen, product]);

  // Separate effect to handle variant data when it becomes available
  useEffect(() => {
    if (isOpen && product?.variants && Array.isArray(product.variants) && variantData && variantData.length > 0) {
      const initialVariants: { [key: number]: number } = {};
      let detectedVariantType: number | null = null;

      // Group product variants by variant name to detect the type
      const variantsByName: { [key: string]: any[] } = {};
      product.variants.forEach((productVariant: any) => {
        if (productVariant && productVariant.variantName && productVariant.variantValue) {
          if (!variantsByName[productVariant.variantName]) {
            variantsByName[productVariant.variantName] = [];
          }
          variantsByName[productVariant.variantName].push(productVariant);
        }
      });

      // Find the variant type in our data structure and map values to option IDs
      Object.keys(variantsByName).forEach((variantName) => {
        const matchingVariantType = variantData.find((vt) => vt.name === variantName);
        if (matchingVariantType && !detectedVariantType) {
          detectedVariantType = matchingVariantType.id;

          // Map product variants to option IDs and stock
          variantsByName[variantName].forEach((productVariant) => {
            // Find the matching option by value
            const matchingOption = matchingVariantType.options?.find(
              (opt) => opt.value === productVariant.variantValue,
            );
            if (matchingOption) {
              initialVariants[matchingOption.id] = productVariant.stock || 0;
            }
          });
        }
      });

      // Always set the detected variant type if we found one
      if (detectedVariantType) {
        setSelectedVariantType(detectedVariantType);
      }

      // Always set the initial variants if we found any
      if (Object.keys(initialVariants).length > 0) {
        setSelectedVariants(initialVariants);
      }
    } else if (isOpen && product && (!product.variants || product.variants.length === 0)) {
      // If product has no variants, reset the variant state
      setSelectedVariantType(null);
      setSelectedVariants({});
    }
  }, [variantData, isOpen, product]);

  // Error handling
  if (variantError) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Hata</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="error">
              <AlertIcon />
              Varyant bilgileri yüklenirken bir hata oluştu.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Kapat</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflow="hidden">
        <ModalHeader>{product ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</ModalHeader>
        <ModalCloseButton />

        <form onSubmit={formik.handleSubmit}>
          <ModalBody overflowY="auto" maxH="calc(90vh - 120px)">
            {isLoadingVariants ? (
              <Center py={10}>
                <Spinner size="lg" />
              </Center>
            ) : (
              <VStack spacing={6} align="stretch">
                {/* Basic Info */}
                <Box>
                  <Text fontSize="lg" fontWeight="bold" mb={4}>
                    Temel Bilgiler
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={!!(formik.touched?.name && formik.errors?.name)}>
                      <FormLabel>Ürün Adı *</FormLabel>
                      <Input
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Ürün adını girin"
                      />
                      <FormErrorMessage>{formik.errors.name as string}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!(formik.touched.title && formik.errors.title)}>
                      <FormLabel>Başlık *</FormLabel>
                      <Input
                        name="title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Ürün başlığını girin"
                      />
                      <FormErrorMessage>{formik.errors.title as string}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!(formik.touched.price && formik.errors.price)}>
                      <FormLabel>Fiyat (TL) *</FormLabel>
                      <NumberInput
                        min={0}
                        value={formik.values.price}
                        onChange={(_, valueAsNumber) => formik.setFieldValue('price', valueAsNumber || 0)}
                      >
                        <NumberInputField placeholder="0" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <FormErrorMessage>{formik.errors.price as string}</FormErrorMessage>
                    </FormControl>

                    <FormControl
                      isInvalid={!!(formik.touched.expertisePointGroupType && formik.errors.expertisePointGroupType)}
                    >
                      <FormLabel>Marka *</FormLabel>
                      <Select
                        name="expertisePointGroupType"
                        value={formik.values.expertisePointGroupType}
                        onChange={formik.handleChange}
                      >
                        {Array.from(ExpertisePointGroupTypeLabel.entries()).map(([key, value]) => (
                          <option key={key} value={key}>
                            {value}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>{formik.errors.expertisePointGroupType as string}</FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl mt={4}>
                    <FormLabel>Açıklama</FormLabel>
                    <Textarea
                      name="description"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      placeholder="Ürün açıklaması (opsiyonel)"
                      rows={3}
                    />
                  </FormControl>

                  <FormControl mt={4}>
                    <Checkbox
                      name="isActive"
                      isChecked={formik.values.isActive}
                      onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                    >
                      Aktif
                    </Checkbox>
                  </FormControl>
                </Box>

                <Divider />

                {/* Photo Upload */}
                <Box>
                  <Text fontSize="lg" fontWeight="bold" mb={4}>
                    Ürün Görselleri *
                  </Text>
                  <ImageUploader value={photoUrls} onChange={handlePhotoUrlsChange} maxImages={5} />
                  {photoUrls.length === 0 && (
                    <Text color="red.500" fontSize="sm" mt={2}>
                      En az 1 görsel gerekli
                    </Text>
                  )}
                </Box>

                <Divider />

                {/* Variants */}
                <Box>
                  <Text fontSize="lg" fontWeight="bold" mb={4}>
                    Varyantlar ve Stok *
                  </Text>
                  {variantData && variantData.length > 0 ? (
                    <VStack spacing={4} align="stretch">
                      {/* Variant Type Selection */}
                      <FormControl>
                        <FormLabel>Varyant Tipi Seçin</FormLabel>
                        <RadioGroup value={selectedVariantType?.toString() || ''} onChange={handleVariantTypeChange}>
                          <Stack direction="row" wrap="wrap" spacing={4}>
                            {variantData.map((variant) => (
                              <Radio key={variant.id} value={variant.id.toString()}>
                                {variant.name}
                              </Radio>
                            ))}
                          </Stack>
                        </RadioGroup>
                      </FormControl>

                      {/* Variant Options for Selected Type */}
                      {selectedVariantType && (
                        <Box>
                          {(() => {
                            const selectedVariant = variantData.find((v) => v.id === selectedVariantType);
                            if (!selectedVariant) return null;

                            return (
                              <Box p={4} borderWidth={1} borderRadius="md" borderColor={borderColor}>
                                <Text fontWeight="semibold" mb={3}>
                                  {selectedVariant.name} Seçenekleri
                                </Text>
                                {selectedVariant.name.toLowerCase().includes('adet') ? (
                                  // For "adet" type, use radio buttons for single selection with stock input
                                  <VStack spacing={4} align="stretch">
                                    <RadioGroup
                                      value={Object.keys(selectedVariants)[0] || ''}
                                      onChange={handleRadioVariantChange}
                                    >
                                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                                        {selectedVariant.options?.map((option) => (
                                          <Box key={option.id} p={3} bg={variantBg} borderRadius="md">
                                            <Radio value={option.id.toString()}>
                                              <Text fontSize="sm" fontWeight="medium">
                                                {option.value}
                                              </Text>
                                            </Radio>
                                          </Box>
                                        ))}
                                      </SimpleGrid>
                                    </RadioGroup>

                                    {/* Stock input for selected adet option */}
                                    {Object.keys(selectedVariants).length > 0 && (
                                      <Box p={3} bg={variantBg} borderRadius="md" maxW="200px">
                                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                                          Stok Miktarı:
                                        </Text>
                                        <NumberInput
                                          size="sm"
                                          min={1}
                                          value={Object.values(selectedVariants)[0] || 1}
                                          onChange={(_, valueAsNumber) => {
                                            const selectedOptionId = parseInt(Object.keys(selectedVariants)[0], 10);
                                            handleAdetStockChange(selectedOptionId, valueAsNumber || 1);
                                          }}
                                        >
                                          <NumberInputField placeholder="Stok" />
                                          <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                          </NumberInputStepper>
                                        </NumberInput>
                                      </Box>
                                    )}
                                  </VStack>
                                ) : (
                                  // For other types (like "beden"), use checkboxes with stock input
                                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                                    {selectedVariant.options?.map((option) => (
                                      <Box key={option.id} p={3} bg={variantBg} borderRadius="md">
                                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                                          {option.value}
                                        </Text>
                                        <NumberInput
                                          size="sm"
                                          min={0}
                                          value={selectedVariants[option.id] || 0}
                                          onChange={(_, valueAsNumber) =>
                                            handleVariantChange(option.id, valueAsNumber || 0)
                                          }
                                        >
                                          <NumberInputField placeholder="Stok" />
                                          <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                          </NumberInputStepper>
                                        </NumberInput>
                                      </Box>
                                    ))}
                                  </SimpleGrid>
                                )}
                              </Box>
                            );
                          })()}
                        </Box>
                      )}

                      {selectedVariantType && Object.keys(selectedVariants).length === 0 && (
                        <Text color={errorColor} fontSize="sm">
                          En az 1 varyant seçimi gerekli
                        </Text>
                      )}

                      {Object.keys(selectedVariants).length > 0 && (
                        <Box>
                          <Text fontSize="sm" color={textSecondary} mb={2}>
                            Seçili Varyantlar:
                          </Text>
                          <HStack wrap="wrap" spacing={2}>
                            {Object.entries(selectedVariants).map(([variantOptionId, stockQuantity]) => {
                              const option = variantData
                                ?.flatMap((v) => v.options || [])
                                .find((o) => o.id.toString() === variantOptionId);
                              return option ? (
                                <Badge key={variantOptionId} colorScheme="blue" variant="outline">
                                  {option.value}: {stockQuantity} adet
                                </Badge>
                              ) : null;
                            })}
                          </HStack>
                        </Box>
                      )}
                    </VStack>
                  ) : (
                    <Alert status="warning">
                      <AlertIcon />
                      Varyant seçenekleri bulunamadı.
                    </Alert>
                  )}
                </Box>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              İptal
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={saveProductMutation.isLoading}
              loadingText="Kaydediliyor..."
              isDisabled={
                !formik.isValid ||
                photoUrls.length === 0 ||
                (!product && (!selectedVariantType || Object.keys(selectedVariants).length === 0))
              }
            >
              {product ? 'Güncelle' : 'Kaydet'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
